import { useRef, useState } from 'react'
import { errorMessage } from '../lib/api'
import { useUploadPlan } from '../lib/hooks'
import type { ParkDetail } from '../lib/types'

/**
 * De plattegrond aanleveren.
 *
 * Dit is de eerste stap voor een nieuw park en meteen de stap waarop alles rust: de afmetingen van de
 * tekening zijn het coördinatenstelsel van de kaart. Daarom staat hij bovenaan, boven de velden die je
 * daarna nog met de hand kunt bijstellen, en zegt hij na afloop wat er precies is gebeurd.
 */
export default function PlanUpload({ detail }: { detail: ParkDetail }) {
  const upload = useUploadPlan(detail.park.slug)
  const invoer = useRef<HTMLInputElement>(null)
  const [sleept, setSleept] = useState(false)

  const kies = (bestanden: FileList | null) => {
    const bestand = bestanden?.[0]
    if (bestand) upload.mutate(bestand)
  }

  const bezig = upload.isPending
  const klaar = upload.data

  return (
    <div className="card">
      <h2>Plattegrond aanleveren</h2>
      <p className="muted small">
        De tekening bepaalt waar de plaatsen liggen: elke plaats ligt op een pixel ervan. Lever aan als PDF,
        PNG, JPG of SVG. Een bestand uit Illustrator kan rechtstreeks, want dat is een PDF met een eigen kop
        erop.
      </p>

      <div
        className={`plan-drop${sleept ? ' over' : ''}${bezig ? ' bezig' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setSleept(true)
        }}
        onDragLeave={() => setSleept(false)}
        onDrop={(e) => {
          e.preventDefault()
          setSleept(false)
          if (!bezig) kies(e.dataTransfer.files)
        }}
      >
        <input
          ref={invoer}
          type="file"
          accept=".pdf,.ai,.eps,.png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml,application/pdf"
          hidden
          onChange={(e) => {
            kies(e.target.files)
            e.target.value = ''
          }}
        />
        {bezig ? (
          <>
            <span className="spinner" aria-hidden="true" />
            <span>Bezig met omzetten en opmeten…</span>
          </>
        ) : (
          <>
            <strong>Sleep de tekening hierheen</strong>
            <span className="muted small">of</span>
            <button type="button" className="btn" onClick={() => invoer.current?.click()}>
              Kies een bestand
            </button>
          </>
        )}
      </div>

      {upload.isError && <p className="inline-error">{errorMessage(upload.error)}</p>}

      {klaar && !bezig && (
        <div className="state">
          <p className="inline-ok">
            {klaar.originalName} staat erop: {klaar.width} × {klaar.height} pixels.
          </p>
          {klaar.scaled && (
            <p className="notice">
              De nieuwe tekening heeft een ander formaat dan de vorige, dus alle plaatsen, velden en
              voorzieningen zijn evenredig meeverschoven. Loop ze na op de kaart voordat je publiceert.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
